/**
 * DubbeleReserveringenDialog — Stitch design
 * Shows two duplicate reservations side-by-side with deltas,
 * submitter info, and option to keep one / ignore.
 */
import React, { useMemo, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Tooltip } from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd', tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const MaterialListTooltip = ({ materialIds, materialNames }) => {
  if (!materialIds || materialIds.length === 0) return null;
  const names = materialIds.map(id => materialNames[id] || id).sort();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 300, overflow: 'auto' }}>
      {names.map((n, i) => (
        <Typography key={i} sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: '#fff' }}>{n}</Typography>
      ))}
    </Box>
  );
};

const DubbeleReserveringenDialog = ({ open, onClose, draaidag, reserveringen = [], onKeep }) => {
  const [materialNames, setMaterialNames] = useState({});

  // Fetch all materials and build name map
  useEffect(() => {
    if (!open || reserveringen.length < 2) return;

    const fetchNames = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.materials.list());
        const map = {};
        (res.data || []).forEach(m => { map[m.id] = m.fields?.Naam || m.id; });
        setMaterialNames(map);
      } catch (err) {
        console.error('[DubbeleRes] Failed to fetch material names:', err);
      }
    };
    fetchNames();
  }, [open, reserveringen]);

  // Compute material deltas between reservations
  const comparison = useMemo(() => {
    if (reserveringen.length < 2) return null;
    const a = reserveringen[0];
    const b = reserveringen[1];
    const matsA = new Set(a?.fields?.Materiaal || []);
    const matsB = new Set(b?.fields?.Materiaal || []);
    const onlyA = [...matsA].filter(m => !matsB.has(m));
    const onlyB = [...matsB].filter(m => !matsA.has(m));
    const shared = [...matsA].filter(m => matsB.has(m));
    return { a, b, matsA, matsB, onlyA, onlyB, shared };
  }, [reserveringen]);

  if (!comparison) return null;

  const { a, b, matsA, matsB, onlyA, onlyB, shared } = comparison;
  const draaidagNaam = draaidag?.fields?.Draaidag || 'Onbekende draaidag';

  const ResCard = ({ res, matSet, uniqueIds, isLeft }) => {
    const f = res?.fields || {};
    const nummer = f.reserveringNummer || f.ReserveringNummer || '?';
    const submitter = f.SubmitterName || f.ModifiedByName || null;
    const createdAt = f.CreatedAt;
    const start = f.Start;
    const eind = f.Eind;
    const otherId = isLeft ? b.id : a.id;
    const allIds = [...matSet];

    return (
      <Box sx={{
        flex: 1, minWidth: 0, p: 2, borderRadius: '0.75rem',
        bgcolor: T.surfaceLow, border: `1px solid ${T.outlineVariant}30`,
        display: 'flex', flexDirection: 'column', gap: 1.5,
      }}>
        {/* Header */}
        <Box>
          <Typography sx={{ fontFamily: T.font, fontSize: '0.95rem', fontWeight: 800, color: T.text }}>
            {nummer}
          </Typography>
          {submitter ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
              <Icon name="person" size={14} sx={{ color: T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>{submitter}</Typography>
            </Box>
          ) : (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant, mt: 0.5 }}>
              Aanmaker onbekend
            </Typography>
          )}
          {createdAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
              <Icon name="schedule" size={13} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outlineVariant }}>
                {fmtDate(createdAt)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {start && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon name="calendar_today" size={14} sx={{ color: T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>
                {start}{eind && eind !== start ? ` – ${eind}` : ''}
              </Typography>
            </Box>
          )}
          <Tooltip arrow placement="bottom-start" title={<MaterialListTooltip materialIds={allIds} materialNames={materialNames} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
              <Icon name="inventory_2" size={14} sx={{ color: T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, borderBottom: `1px dashed ${T.outlineVariant}` }}>
                {matSet.size} materialen
              </Typography>
            </Box>
          </Tooltip>
          {uniqueIds.length > 0 && (
            <Tooltip arrow placement="bottom-start" title={<MaterialListTooltip materialIds={uniqueIds} materialNames={materialNames} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                <Icon name="difference" size={14} sx={{ color: T.amber }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.amber, borderBottom: `1px dashed ${T.amber}60` }}>
                  {uniqueIds.length} uniek
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Notitie */}
        {f.Notities ? (
          <Box sx={{ px: 1.25, py: 0.75, borderRadius: '0.375rem', bgcolor: T.surface, border: `1px solid ${T.outlineVariant}25` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.25 }}>
              <Icon name="sticky_note_2" size={12} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 600, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Notitie</Typography>
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, lineHeight: 1.4, maxHeight: 60, overflow: 'auto' }}>
              {f.Notities}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outlineVariant, fontStyle: 'italic' }}>
            Geen notitie
          </Typography>
        )}

        {/* Keep button */}
        <Box
          onClick={() => onKeep(res.id, otherId)}
          sx={{
            mt: 'auto', px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
            bgcolor: T.emerald, color: '#fff', textAlign: 'center',
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
            transition: 'all 0.15s',
            '&:hover': { filter: 'brightness(0.9)' },
          }}
        >
          <Icon name="check" size={16} sx={{ color: '#fff', verticalAlign: 'middle', mr: 0.5 }} />
          Behoud deze
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '1rem', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.surfaceHigh}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '0.5rem', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="content_copy" size={18} sx={{ color: T.error }} />
          </Box>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>
            Dubbele reservering
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.5 }}>
          Deze draaidag heeft twee reserveringen. Kies welke je wilt behouden — de andere wordt verwijderd.
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        {/* Draaidag naam */}
        <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: '0.5rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 600, color: T.text }}>
            {draaidagNaam}
          </Typography>
        </Box>

        {/* Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Tooltip arrow title={<MaterialListTooltip materialIds={shared} materialNames={materialNames} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'default' }}>
              <Icon name="join_inner" size={16} sx={{ color: T.emerald }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, borderBottom: `1px dashed ${T.outlineVariant}` }}>
                {shared.length} gedeeld
              </Typography>
            </Box>
          </Tooltip>
          {onlyA.length > 0 && (
            <Tooltip arrow title={<MaterialListTooltip materialIds={onlyA} materialNames={materialNames} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'default' }}>
                <Icon name="arrow_back" size={14} sx={{ color: T.primary }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, borderBottom: `1px dashed ${T.outlineVariant}` }}>
                  {onlyA.length} alleen links
                </Typography>
              </Box>
            </Tooltip>
          )}
          {onlyB.length > 0 && (
            <Tooltip arrow title={<MaterialListTooltip materialIds={onlyB} materialNames={materialNames} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'default' }}>
                <Icon name="arrow_forward" size={14} sx={{ color: T.primary }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, borderBottom: `1px dashed ${T.outlineVariant}` }}>
                  {onlyB.length} alleen rechts
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Side by side cards */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <ResCard res={a} matSet={matsA} uniqueIds={onlyA} isLeft={true} />
          <ResCard res={b} matSet={matsB} uniqueIds={onlyB} isLeft={false} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, borderTop: `1px solid ${T.surfaceHigh}` }}>
        <Box onClick={onClose} sx={{
          px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer',
          fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem',
          color: T.secondary, border: `1.5px solid ${T.outlineVariant}40`,
          transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceLow },
        }}>
          Negeren
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DubbeleReserveringenDialog;
